import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { PricingHero } from "@/components/site/PricingHero";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { primaryVideoObject } from "@/lib/video-seo";
import { safeJsonLd } from "@/lib/structured-data-core";
import { doctors } from "@/lib/site-data";
import { PRICING_DOCTORS, PRICING_FAQ, PRICING_GUARANTEE, PRICING_META } from "@/lib/wp-restore/pricing";

const PricingFaqAccordion = lazy(() =>
  import("@/components/site/PricingFaqAccordion").then((m) => ({ default: m.PricingFaqAccordion })),
);
const Guarantee = lazy(() => import("@/components/site/Guarantee").then((m) => ({ default: m.Guarantee })));
const PricingClientResults = lazy(() =>
  import("@/components/site/PricingClientResults").then((m) => ({ default: m.PricingClientResults })),
);
const Faq = lazy(() => import("@/components/site/Faq").then((m) => ({ default: m.Faq })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then((m) => ({ default: m.DoctorsGrid })));

// Doctor cards for the WordPress doctors row. Dr. Michael Lee is not on the live site, so he is not listed.
const pricingDoctors = PRICING_DOCTORS.map((d) => ({
  ...(doctors.find((x) => x.name === d.name) as (typeof doctors)[number]),
  role: d.role.replace(/<br\s*\/?>/g, " ").replace(/&amp;/g, "&"),
}));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: PRICING_META.title },
      { name: "description", content: PRICING_META.description },
      { property: "og:title", content: "Hair Transplant Pricing | Save $1,000 — RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Invest in yourself. Save $1,000 on your FUE hair transplant. Low-interest financing available.",
      },
      { property: "og:url", content: "https://restorehair.com/pricing/" },
    ],
    links: [
      { rel: "canonical", href: "https://restorehair.com/pricing/" },
      {
        rel: "preload",
        as: "image",
        href: "https://assets.restorehair.com/lp/zach-kevin-hero.webp",
        fetchpriority: "high",
      },
    ],
    scripts: [{
      type: "application/ld+json",
      children: safeJsonLd(primaryVideoObject("/pricing/")),
    }],
  }),
  component: PricingPage,
});

function PricingPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <PricingHero />
        <NorwoodQuiz />
        {/* The long-form cost guide. /pricing/ is the price page, the guide
            explains what drives the price; each links to the other (17 Oct
            2026, RESTORE's search cleanup log). */}
        <section className="bg-background pt-10 md:pt-14">
          <p className="mx-auto max-w-3xl px-4 text-center text-base text-foreground md:text-lg">
            What makes one hair transplant cost more than another?{" "}
            <a
              href="/the-true-hair-restoration-cost/"
              className="font-bold text-primary underline underline-offset-2 hover:opacity-80"
            >
              Read our hair transplant cost guide
            </a>
          </p>
        </section>
        <Suspense fallback={null}>
          <PricingFaqAccordion />
          <DoctorsGrid doctors={pricingDoctors} nameTag="h2" />
          <Guarantee wpCopy={{ ...PRICING_GUARANTEE, bodyTag: "h2" as const }} />
          <PricingClientResults />
          <Faq
            items={PRICING_FAQ.items}
           
            numbered
            headingTag="h3"
            questionTag="h4"
          />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}